/**
 * Date math shared by the "Schedule REC in Roger" intake flow (recert
 * onboarding for patients migrated into Roger mid-admission).
 *
 * Terminology (matches the backend REC push validation in
 * backend/app/api/intake.py):
 *   N = New Episode Start Date (episode_start_date)
 *   A = Original Admission/SOC Date (soc_date)
 *   R = Recertification Date (fields.recert_date)
 *
 * The push creates a previous episode [N-60, N-1] and an upcoming episode
 * [N, N+59], with the REC visit dated R on the PREVIOUS episode.
 */
import {
  daysBetween,
  formatDateToISO,
  getTodayInPT,
  parseISOToLocalDate,
} from './dateUtils';

/** Normalize to the YYYY-MM-DD part, or null when absent/blank. */
export function isoDateOnly(value: string | null | undefined): string | null {
  if (!value || typeof value !== 'string') return null;
  const datePart = value.trim().split('T')[0];
  return /^\d{4}-\d{2}-\d{2}$/.test(datePart) ? datePart : null;
}

/** Add (or subtract) whole days to a YYYY-MM-DD string; null when unparseable. */
export function addDaysToISODate(
  value: string | null | undefined,
  days: number
): string | null {
  const dateOnly = isoDateOnly(value);
  const parsed = parseISOToLocalDate(dateOnly ?? undefined);
  if (!parsed) return null;
  parsed.setDate(parsed.getDate() + days);
  return formatDateToISO(parsed);
}

/** Tomorrow's calendar date in Pacific Time as YYYY-MM-DD (REC date default). */
export function tomorrowInPTISODate(): string {
  // getTodayInPT is luxon-based and always yields a valid ISO date.
  const tomorrow = addDaysToISODate(getTodayInPT(), 1);
  if (!tomorrow) throw new Error('Failed to compute tomorrow in Pacific Time');
  return tomorrow;
}

/** Whole-day difference b - a between two YYYY-MM-DD strings; null when unparseable. */
export function daysBetweenISODates(
  a: string | null | undefined,
  b: string | null | undefined
): number | null {
  const dateA = parseISOToLocalDate(isoDateOnly(a) ?? undefined);
  const dateB = parseISOToLocalDate(isoDateOnly(b) ?? undefined);
  if (!dateA || !dateB) return null;
  return daysBetween(dateA, dateB);
}

/** Current form dates the REC auto-chain reads when deciding whether to follow. */
export interface RecChainCurrentDates {
  recertDate?: string | null;
  episodeStartDate?: string | null;
  socDate?: string | null;
}

/**
 * The A = N-60 step of the chain, shared by both patch builders: the SOC date
 * follows a new episode start only while its current value is chain-consistent
 * — empty, or exactly what the current episode start produced.
 */
function socChainPatch(
  newStart: string,
  currentStart: string | null,
  currentSocDate: string | null | undefined
): { soc_date?: string } {
  const currentSoc = isoDateOnly(currentSocDate);
  const chainSoc = currentStart ? addDaysToISODate(currentStart, -60) : null;
  if (currentSoc && currentSoc !== chainSoc) return {};
  const admission = addDaysToISODate(newStart, -60);
  return admission ? { soc_date: admission } : {};
}

/**
 * Patch for a Recertification Date change: N = R+1 and A = N-60 follow along
 * only while their current values are chain-consistent — empty, or exactly
 * what the previous chain produced. Deriving "manually edited" from the values
 * themselves (rather than a mount-scoped ref) survives tab-switch remounts,
 * which reset any ref state and previously re-clobbered corrected dates.
 */
export function buildRecertChainPatch(
  newValue: string | null | undefined,
  current: RecChainCurrentDates
): {
  recert_date: string | null;
  episode_start_date?: string;
  soc_date?: string;
} {
  const patch: {
    recert_date: string | null;
    episode_start_date?: string;
    soc_date?: string;
  } = { recert_date: newValue ?? null };
  const newRecert = isoDateOnly(newValue);
  if (!newRecert) return patch;

  const oldRecert = isoDateOnly(current.recertDate);
  const currentStart = isoDateOnly(current.episodeStartDate);
  const chainStart = oldRecert ? addDaysToISODate(oldRecert, 1) : null;
  if (currentStart && currentStart !== chainStart) return patch;

  const newStart = addDaysToISODate(newRecert, 1);
  if (!newStart) return patch;
  patch.episode_start_date = newStart;

  return {
    ...patch,
    ...socChainPatch(newStart, currentStart, current.socDate),
  };
}

/** Patch for a New Episode Start Date change: A = N-60 follows while chain-consistent. */
export function buildEpisodeStartChainPatch(
  newValue: string | null | undefined,
  current: Pick<RecChainCurrentDates, 'episodeStartDate' | 'socDate'>
): { episode_start_date: string | null; soc_date?: string } {
  const patch: { episode_start_date: string | null; soc_date?: string } = {
    episode_start_date: newValue ?? null,
  };
  const newStart = isoDateOnly(newValue);
  if (!newStart) return patch;

  const currentStart = isoDateOnly(current.episodeStartDate);
  return {
    ...patch,
    ...socChainPatch(newStart, currentStart, current.socDate),
  };
}

/**
 * Default dates stamped when "Schedule REC in Roger" is selected with no
 * recert date yet: R = tomorrow, N = R+1, A = N-60. Selection time is the one
 * moment episode start / SOC date are known to carry meaningless SOC-flow
 * leftovers, so the whole chain is written; selecting REC when a recert date
 * already exists applies nothing. (Stamping at selection — not in a form mount
 * effect — keeps tab-switch remounts and intake switches from re-defaulting
 * over user-corrected dates.)
 */
export function getRecSelectionDateDefaults(
  currentRecertDate: string | null | undefined
): { recert_date?: string; episode_start_date?: string; soc_date?: string } {
  if (isoDateOnly(currentRecertDate)) return {};
  const tomorrow = tomorrowInPTISODate();
  const newStart = addDaysToISODate(tomorrow, 1);
  const admission = newStart ? addDaysToISODate(newStart, -60) : null;
  return {
    recert_date: tomorrow,
    ...(newStart ? { episode_start_date: newStart } : {}),
    ...(admission ? { soc_date: admission } : {}),
  };
}

/** Derived episode/visit dates for the REC summary panel and validation messages. */
export function getRecDerivedDates(
  episodeStartDate: string | null | undefined
): {
  previousEpisodeStart: string;
  previousEpisodeEnd: string;
  upcomingEpisodeStart: string;
  upcomingEpisodeEnd: string;
  recertWindowStart: string;
  recertWindowEnd: string;
} | null {
  const start = isoDateOnly(episodeStartDate);
  if (!start) return null;
  const previousEpisodeStart = addDaysToISODate(start, -60);
  const previousEpisodeEnd = addDaysToISODate(start, -1);
  const upcomingEpisodeEnd = addDaysToISODate(start, 59);
  const recertWindowStart = addDaysToISODate(start, -5);
  if (
    !previousEpisodeStart ||
    !previousEpisodeEnd ||
    !upcomingEpisodeEnd ||
    !recertWindowStart
  ) {
    return null;
  }
  return {
    previousEpisodeStart,
    previousEpisodeEnd,
    upcomingEpisodeStart: start,
    upcomingEpisodeEnd,
    recertWindowStart,
    recertWindowEnd: previousEpisodeEnd,
  };
}
